// Root layout: header + sidebar + <Outlet/>, plus an auth guard that
// redirects between /login and / as needed.
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth, useLogout } from './features/auth/useAuth';
import { SessionProvider } from './contexts/SessionContext';
import { GraphStorageProvider } from './contexts/GraphStorageContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ZoomProvider } from './contexts/ZoomContext';
import { useGlobalTooltip } from './hooks/useGlobalTooltip';
import Sidebar from './features/sidebar/Sidebar';
import HeaderDropdown from './components/HeaderDropdown';
import malpediaLogo from './assets/malpedia-logo.png';
import malpediaLogoDark from './assets/malpedia-logo-dark.png';

export default function App() {
  return (
    <ThemeProvider>
      <ZoomProvider>
        <AppShell />
      </ZoomProvider>
    </ThemeProvider>
  );
}

function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: user, isLoading } = useAuth();
  const logout = useLogout();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { resolved } = useTheme();
  useGlobalTooltip();

  const onLoginPage = location.pathname === '/login';
  const authed = !!user;

  // Auth guard: bounce between /login and / depending on session.
  useEffect(() => {
    if (isLoading) return;
    if (!authed && !onLoginPage) {
      navigate('/login', { replace: true });
    } else if (authed && onLoginPage) {
      navigate('/', { replace: true });
    }
  }, [authed, isLoading, onLoginPage, navigate]);

  const handleLogout = async () => {
    try {
      await logout.mutateAsync();
      toast.success('Signed out');
      navigate('/login', { replace: true });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Logout failed');
    }
  };

  // While auth is still being resolved, render a minimal placeholder so
  // child routes don't flash before the guard can act.
  if (isLoading) {
    return <div className="malpedia-app-loading">Loading…</div>;
  }

  return (
    <div className="malpedia-app-shell">
      {!onLoginPage && (
        <header className="malpedia-app-header">
          <Link to="/" className="malpedia-app-brand">
            <img
              src={resolved === 'dark' ? malpediaLogoDark : malpediaLogo}
              alt="Malpedia"
              className="malpedia-app-brand-logo"
            />
            <span>RAG</span>
          </Link>
          {user && (
            <div className="malpedia-app-header-right">
              <HeaderDropdown
                user={user}
                onLogout={handleLogout}
                logoutPending={logout.isPending}
              />
            </div>
          )}
        </header>
      )}
      {onLoginPage ? (
        <main className="malpedia-app-main">
          <Outlet />
        </main>
      ) : (
        <SessionProvider>
          <GraphStorageProvider>
            <div className="malpedia-app-body">
              <Sidebar
                collapsed={sidebarCollapsed}
                onToggleCollapse={() => setSidebarCollapsed((v) => !v)}
              />
              <main className="malpedia-app-main">
                <Outlet />
              </main>
            </div>
          </GraphStorageProvider>
        </SessionProvider>
      )}
    </div>
  );
}
